// Drop-zone (ColumnGroup) icons.
//
// The icon belongs to the VISUAL DEFINITION: every ColumnGroup in
// `backend/app/data/DashboardVisuals.json` carries a `GroupIcon` token
// ("rows", "values", "top-left", "hierarchy-level", …). This file only draws
// the token. A model saved before the tokens existed still carries the old
// "*.svg" names (nothing ever rendered those), so the token is derived from
// the group's StandardName instead — the same rule the catalog was built with.

import type { CSSProperties } from 'react'

const BOX = 'M3 3h18v18H3z'
const PALETTE = 'M12 3a9 9 0 1 0 0 18c1 0 1.5-.8 1.5-1.5 0-.9-.7-1.3-.7-2.2 0-1 .8-1.8 1.8-1.8H17a4 4 0 0 0 4-4c0-4.7-4-8.5-9-8.5z'
const FOLDER = 'M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z'
const TAG = 'M3 12V4h8l10 10-8 8zM7.5 7.5h.01'
const PIN = 'M12 21s7-6 7-11a7 7 0 1 0-14 0c0 5 7 11 7 11zM12 10h.01'

const PATHS: Record<string, string> = {
  rows: 'M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01',
  columns: 'M4 4h4v16H4zM10 4h4v16h-4zM16 4h4v16h-4z',
  values: 'M18 4H6l6 8-6 8h12',
  value: 'M5 9h14M5 15h14M10 3L8 21M16 3l-2 18',
  stack: 'M12 2l9 5-9 5-9-5zM3 12l9 5 9-5M3 17l9 5 9-5',
  lines: 'M3 17l6-6 4 4 8-8',
  category: TAG,
  label: TAG,
  stage: 'M3 4h18l-7 8v6l-4 3v-9z',
  axis: 'M12 3v9l7 5M12 12l-7 5',
  series: PALETTE,
  color: PALETTE,
  parent: FOLDER,
  groups: FOLDER,
  source: 'M4 12h12M12 6l6 6-6 6',
  target: 'M3 12a9 9 0 1 0 18 0a9 9 0 1 0-18 0M8 12a4 4 0 1 0 8 0a4 4 0 1 0-8 0',
  min: 'M4 20h16M12 4v12M8 12l4 4 4-4',
  max: 'M4 4h16M12 20V8M8 12l4-4 4 4',
  date: 'M4 5h16v15H4zM4 10h16M9 3v4M15 3v4',
  fields: 'M6 2h9l5 5v15H6zM14 2v6h6M9 13h8M9 17h6',
  footer: BOX + 'M7 16h10',
  'top-left': BOX + 'M6 6h6v3H6z',
  'top-right': BOX + 'M12 6h6v3h-6z',
  'center-first-row': BOX + 'M6 10h12v3H6z',
  'center-second-row': BOX + 'M8 14h8v2H8z',
  'bottom-left': BOX + 'M6 16h6v2H6z',
  'bottom-right': BOX + 'M12 16h6v2h-6z',
  'first-row-left': 'M3 5h9v4H3zM3 14h18M3 19h18',
  'first-row-right': 'M12 5h9v4h-9zM3 14h18M3 19h18',
  'second-row-left': 'M3 5h18M3 10h9v4H3zM3 19h18',
  'second-row-right': 'M3 5h18M12 10h9v4h-9zM3 19h18',
  'third-row-left': 'M3 5h18M3 10h18M3 15h9v4H3z',
  'third-row-right': 'M3 5h18M3 10h18M12 15h9v4h-9z',
  'total-value': 'M3 12a9 9 0 1 0 18 0a9 9 0 1 0-18 0',
  'achievement-value': 'M12 3a9 9 0 1 1-9 9M12 7a5 5 0 1 1-5 5',
  'hierarchy-level': 'M3 3h6v4H3zM14 10h7v4h-7zM6 7v5h8',
  latitude: PIN,
  longitude: PIN,
  region: 'M9 4L3 6v14l6-2 6 2 6-2V4l-6 2zM9 4v14M15 6v14',
  size: 'M8 16a3 3 0 1 0 0.1 0M16 9a5 5 0 1 0 0.1 0',
  events: 'M4 22V4M4 4h12l-2 4 2 4H4',
}
const FALLBACK = 'M4 4h16v16H4z'

/** StandardName → token, the rule the catalog's GroupIcon values follow:
 *  trailing digits dropped, camelCase to kebab-case ("TopLeft" → "top-left"). */
export function tokenForStandardName(std: string): string {
  return (std || '').replace(/\d+$/, '').replace(/(?<!^)(?=[A-Z])/g, '-').toLowerCase()
}

/** The icon token of a drop zone — its GroupIcon when that is a token, else
 *  derived from its StandardName. */
export function slotIconToken(group: { GroupIcon?: string; StandardName?: string }): string {
  const icon = (group.GroupIcon || '').trim()
  if (icon && !/\.(svg|png)$/i.test(icon) && PATHS[icon]) return icon
  return tokenForStandardName(group.StandardName || '')
}

export function SlotIcon({ token, size = 16, style, strokeWidth = 1.9 }: {
  token: string; size?: number; style?: CSSProperties; strokeWidth?: number
}) {
  return (
    <svg
      width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round"
      aria-hidden="true" style={{ flexShrink: 0, ...style }}
    >
      <path d={PATHS[token] || FALLBACK} />
    </svg>
  )
}
